/**
 * OrderLogisticsSection — the Summary step's left-hand recap.
 *
 * The native replacement for legacy `PurchaseFlow/steps/Validation/Logistics`,
 * which was the LAST thing keeping `legacyBridge/` alive: the review canvas and
 * the payment section had already gone native, and every other block in
 * `ValidationController` was either suppressed on a v2 cart or `isEmbed`-gated.
 *
 * A PORT, not a rewrite. The legacy component had already been retrofitted to
 * prefer v2 values (it read `usePfStore`, `useBillingPaymentMethods`,
 * `useInstructionCatalogue`, `useShootDuration`, `resolveDefaultReference`), so
 * this is those branches with every legacy-context fallback removed —
 * `usePurchaseFlowOrderMeta`, `usePurchaseFlowConfig`, `useCart` and
 * `useTargetOrderUser` all disappear with the bridge. Legacy `/purchase-flow`
 * and the Propstack embed keep rendering the original file: the embed mounts the
 * LEGACY flow on its own `/embed/purchase-flow` route, so nothing there changes.
 *
 * One thing the port FIXES rather than copies: the property address. Legacy read
 * it from `selectedAssignmentPlace` (a legacy-config value with no v2 fallback),
 * so on a v2 order the address row depended on the bridge's config shim having
 * resolved. It now reads `property.addressLabel` from the store — the same value
 * the entry step wrote and the cart rail displays.
 *
 * Reads the store at render and never seeds it (MODULE-CONTRACT §11), like every
 * other native step surface.
 */

import React, { Fragment, useMemo } from 'react'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'

import { EventTime } from 'components/common/EventTime'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import {
  DynamicSummary,
  SummaryContactPerson,
  SummaryTextBlock
} from 'components/common/OrderSummaryRows'
import { useTimezone } from 'components/contexts/timezone.context'
import { useUserData } from 'components/contexts/UserDataContext'
import { getTimeDurationStringFromMinutesAndHours } from 'utils/time'

import { usePfStore } from '../../../store'
import { useShootDuration } from '../../../components/cart/useShootDuration'
import { useBillingPaymentMethods } from '../../billing/useBillingPaymentMethods'
import { useIsFreeCart } from '../../billing/chargeable'
import { resolveDefaultReference } from '../../billing/billingDefaults'
import { useInstructionCatalogue } from '../../instructions/useInstructionCatalogue'
import { MEETING_CHOICE_KEY, selectedMeetingChoice } from '../../instructions/meetingChoice.helpers'
import { resolveContactPrefill } from '../../instructions/contactPrefill.helpers'

/**
 * @component
 * Recaps who the order is for, how the creative gets in, and how it is paid.
 *
 * @example
 * <OrderLogisticsSection />
 */
interface Props {
  /** Stacked above the review instead of beside it, so it takes the full width. */
  fullWidth?: boolean
}

export const OrderLogisticsSection: React.FC<Props> = ({ fullWidth = false }) => {
  const { t } = useTranslation(['order', 'purchase_flow_revamp', 'time_translations'])
  const { userTimezone } = useTimezone()

  const meeting = usePfStore((s) => s.meeting)
  const reference = usePfStore((s) => s.reference)
  const property = usePfStore((s) => s.property)
  const targetUserEmail = usePfStore((s) => s.targetUserEmail)
  const paymentMethod = usePfStore((s) => s.paymentMethod)
  const targetUserName = usePfStore((s) => s.targetUserName)
  const targetUserPhone = usePfStore((s) => s.targetUserPhone)
  const { baseUserData } = useUserData()
  /** The same prefill Instructions compares against, so an older draft reads the same card on both steps. */
  const contactPrefill = useMemo(
    () => resolveContactPrefill({ targetUserEmail, targetUserName, targetUserPhone, user: baseUserData }),
    [targetUserEmail, targetUserName, targetUserPhone, baseUserData]
  )
  const ctBookingName = usePfStore((s) => (s.ctBookingId ? s.ctBookingName : undefined))

  const { defaultPaymentMethod } = useBillingPaymentMethods()
  // The billing step asks for no method on a free cart, so recapping one here would assert
  // a decision the client was never offered. Same rule, same helper as that step.
  const isFreeCart = useIsFreeCart()
  const { timezone: shootTimezone } = useInstructionCatalogue()
  const duration = useShootDuration()

  /** The effective method, the same `?? default` the Billing field displays and
   *  `serializePlacement` sends — a client who accepted the BE preselect never
   *  writes the slice (§11.1), so reading it alone would show nothing. */
  const effectivePaymentMethod = paymentMethod ?? defaultPaymentMethod

  /** The chosen mode's own fields, flattened to what this panel renders.
   *  THIRD_PARTY carries no date and no pickup address by design — the third
   *  party arranges the visit, which is the whole point of the mode. */
  const meetingDetails = useMemo(() => {
    const mode = meeting?.mode
    if (!mode || mode === 'NONE') return undefined

    if (mode === 'ON_SITE') {
      const f = meeting?.onSite
      return { date: f?.date, address: undefined, name: f?.contactName, phone: f?.contactPhone, email: f?.contactEmail, comments: f?.comment }
    }

    if (mode === 'KEY_PICKUP') {
      const f = meeting?.keyPickup
      return { date: f?.date, address: f?.address, name: f?.contactName, phone: f?.contactPhone, email: f?.contactEmail, comments: f?.comment }
    }

    const f = meeting?.thirdParty
    return { date: undefined, address: undefined, name: f?.name, phone: f?.phone, email: f?.email, comments: f?.comment }
  }, [meeting])

  /** Only a booking the creative arranges has no date by design; any other missing date is just missing. */
  const dateArrangedByCreative = useMemo(() => {
    if (meeting?.mode === 'THIRD_PARTY') return true
    return meeting?.mode === 'KEY_PICKUP' && !!meeting.keyPickup?.arrangedByCreative
  }, [meeting])

  /** The card the client picked, in its own words: the mode's catalogue name says nothing about who meets the creative. */
  const meetingChoice = selectedMeetingChoice(meeting, contactPrefill)
  const meetingKindLabel = meetingChoice ? t(`purchase_flow_revamp:instructions.meeting.${MEETING_CHOICE_KEY[meetingChoice]}`) : undefined

  /** The prefill from the property title is never written to the store, so
   *  reading the slice alone shows nothing for a client who accepted it. */
  const effectiveReference = reference || resolveDefaultReference(property)

  const hasDataToShow = Boolean(
    targetUserEmail
    || effectiveReference
    || meetingKindLabel
    || meetingDetails
    || property?.addressLabel
    || effectivePaymentMethod
  )

  if (!hasDataToShow) return null

  return (
    <SectionedBorderBox title={t('order:step_validation.logistics')} flex={fullWidth ? '0 0 auto' : '0 0 300px'} height={fullWidth ? 'auto' : '100%'}>
      <Stack gap={2} data-cy="pf-revamp-order-logistics">

        {/* Admin ordering on a client's behalf — the client they picked. */}
        {targetUserEmail &&
          <DynamicSummary title={t('order:step_validation.client')} value={targetUserEmail} />
        }

        {effectiveReference &&
          <DynamicSummary title={t('purchase_flow_revamp:billing.details.reference_label')} value={effectiveReference} />
        }

        {meetingKindLabel &&
          <DynamicSummary title={t('order:step_validation.meeting')} value={meetingKindLabel} />
        }

        {ctBookingName &&
          <DynamicSummary title={t('purchase_flow_revamp:instructions.schedule.selected_creative_label')} value={ctBookingName} />
        }

        {meetingDetails &&
          <Fragment>

            {(meetingDetails.date || dateArrangedByCreative) &&
            <DynamicSummary
              title={t('order:step_validation.date_time')}
              value={meetingDetails.date
                ? (
                  <EventTime
                    eventStart={meetingDetails.date}
                    eventTimezone={shootTimezone ?? null}
                    userTimezone={userTimezone}
                    // An unknown duration sums to zero, which would read "Approx. 0 min".
                    minDuration={duration.max > 0 ? duration.min : undefined}
                    maxDuration={duration.max > 0 ? duration.max : undefined}
                  />
                )
                : t('purchase_flow_revamp:instructions.schedule.to_define_with_creative')
              }
            />
            }

            {/* Dark until the BE sends time-on-site on the v2 catalogue
                — the row hides itself while the sum is zero. */}
            {duration.max > 0 &&
              <DynamicSummary
                title={t('order:cart.duration')}
                value={`${t('time_translations:approx')} ${duration.min > 0 ? `${getTimeDurationStringFromMinutesAndHours(duration.min)} - ` : ''}${getTimeDurationStringFromMinutesAndHours(duration.max)}`}
              />
            }

            {meetingDetails.address &&
              <DynamicSummary
                title={t('order:step_validation.key_pickup_address')}
                value={<SummaryTextBlock value={meetingDetails.address} />}
              />
            }

            {/* The row is titled "Contact person" and the name is
                the first line of the VALUE — passing it as both title and inside
                `SummaryContactPerson` is the doubling bug that report was about.
                Rendered only when there IS a contact, so an empty form prints
                nothing. */}
            {(meetingDetails.name || meetingDetails.phone || meetingDetails.email) &&
              <DynamicSummary
                title={t('order:step_validation.contact_person')}
                value={
                  <SummaryContactPerson
                    name={meetingDetails.name}
                    phone={meetingDetails.phone}
                    email={meetingDetails.email}
                  />
                }
              />
            }

            {meetingDetails.comments &&
              <DynamicSummary
                title={t('order:step_validation.meeting_instructions')}
                value={<SummaryTextBlock value={meetingDetails.comments} />}
              />
            }

          </Fragment>
        }

        {property?.addressLabel &&
          <DynamicSummary
            title={t('order:step_validation.address')}
            value={<SummaryTextBlock value={property.addressLabel} />}
          />
        }

        {/* SX2-F02: B2B clients confirm their billing method before placing —
            unless the order is free, where none was asked for. */}
        {effectivePaymentMethod && !isFreeCart &&
          <DynamicSummary
            title={t('order:step_validation.payment_method')}
            value={t(`purchase_flow_revamp:billing.payment_method.method_${effectivePaymentMethod}`)}
          />
        }

      </Stack>
    </SectionedBorderBox>
  )
}
